import Grid from "@mui/material/Grid";
import {Checkbox, FormControlLabel, FormHelperText, InputLabel, TextField, Select, MenuItem, IconButton, Box, Typography, FormControl, Autocomplete, Paper, Divider} from "@mui/material";
import Button from "@mui/material/Button";
import {Add, Delete, Save} from "@mui/icons-material";
import * as React from "react";
import {useEffect, useState} from "react";
import merchants from "../../models/merchants";

export default function ReferralForm({fields, errors, handleChange, handleBlur, handleSubmit}) {
    const [merchantsList, setMerchantsList] = useState([]);

    useEffect(() => {
        merchants.list().then((data) => {
            console.log('Raw merchants.list() response:', data);
            let merchantsArray = [];
            
            if (Array.isArray(data)) {
                merchantsArray = data;
            } else if (data && Array.isArray(data.merchants)) {
                merchantsArray = data.merchants;
            } else if (data && typeof data === 'object') {
                // Try to extract array from object
                merchantsArray = Object.values(data).find(val => Array.isArray(val)) || [];
            }
            
            console.log('Processed merchants array:', merchantsArray);
            console.log('First merchant:', merchantsArray[0]);
            
            if (merchantsArray.length > 0) {
                setMerchantsList(merchantsArray);
            } else {
                console.warn('No merchants found in response');
                setMerchantsList([]);
            }
        }).catch((err) => {
            console.error('Error loading merchants:', err);
            setMerchantsList([]);
        });
    }, []);

    const handleMerchantChange = (index, field, value) => {
        const newMerchants = [...(fields.merchants || [])];
        if (!newMerchants[index]) {
            newMerchants[index] = {merchant_id: '', percentage: ''};
        }
        newMerchants[index][field] = value;
        handleChange({
            target: {
                name: 'merchants',
                value: newMerchants
            }
        });
    };

    const handleAddMerchant = () => {
        const newMerchants = [...(fields.merchants || []), {merchant_id: '', percentage: ''}];
        handleChange({
            target: {
                name: 'merchants',
                value: newMerchants
            }
        });
    };

    const handleRemoveMerchant = (index) => {
        const newMerchants = [...(fields.merchants || [])];
        newMerchants.splice(index, 1);
        handleChange({
            target: {
                name: 'merchants',
                value: newMerchants
            }
        });
    };

    return <React.Fragment>
        <Grid container spacing={3}
              component="form"
              onSubmit={handleSubmit}
              autoComplete="off"
        >
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>Referral Information</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    error={!!errors.username}
                    label="Username"
                    value={fields.username || ""}
                    name="username"
                    id="username"
                    required={true}
                    fullWidth={true}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={errors.username}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    error={!!errors.password}
                    label="Password"
                    value={fields.password || ""}
                    type="password"
                    autoComplete="new-password"
                    name="password"
                    id="password"
                    required={!fields.id}
                    fullWidth={true}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={fields.id ? 'Leave empty to keep current password' : 'At least 6 characters'}
                    helperText={errors.password || (fields.id ? 'Leave empty to keep current password' : '')}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    error={!!errors.name}
                    label="Name"
                    value={fields.name || ""}
                    name="name"
                    id="name"
                    required={true}
                    fullWidth={true}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={errors.name}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            name="enabled"
                            checked={typeof fields.enabled === 'boolean' ? fields.enabled : false}
                            onChange={handleChange}
                            color="primary"
                        />
                    }
                    label="Enabled"
                />
            </Grid>
            
            <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Merchants & Percentages</Typography>
                    <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddMerchant}
                    >
                        Add Merchant
                    </Button>
                </Box>
                
                {merchantsList.length === 0 && (
                    <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, mb: 2 }}>
                        <Typography variant="body2" color="warning.dark">
                            No merchants available. Please create merchants first.
                        </Typography>
                    </Box>
                )}
                
                {(fields.merchants || []).map((merchant, index) => (
                    <Paper key={index} elevation={1} sx={{ p: 2, mb: 2 }}>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} md={5}>
                                <FormControl fullWidth error={!!errors[`merchants_${index}_merchant_id`]}>
                                    <InputLabel>Merchant *</InputLabel>
                                    <Select
                                        value={merchant.merchant_id ? String(merchant.merchant_id) : ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleMerchantChange(index, 'merchant_id', value ? parseInt(value) : '');
                                        }}
                                        label="Merchant *"
                                        required
                                        displayEmpty
                                    >
                                        <MenuItem value="">
                                            <em>Select merchant</em>
                                        </MenuItem>
                                        {merchantsList.length > 0 ? merchantsList.map((m) => {
                                            if (!m || !m.id) {
                                                console.warn('Invalid merchant object:', m);
                                                return null;
                                            }
                                            const merchantId = String(m.id);
                                            const merchantName = m.name || m.label || 'Unknown';
                                            const merchantUsername = m.username ? ` (${m.username})` : '';
                                            return (
                                                <MenuItem key={merchantId} value={merchantId}>
                                                    {merchantName}{merchantUsername}
                                                </MenuItem>
                                            );
                                        }).filter(Boolean) : (
                                            <MenuItem disabled>No merchants available</MenuItem>
                                        )}
                                    </Select>
                                    {errors[`merchants_${index}_merchant_id`] && (
                                        <FormHelperText>{errors[`merchants_${index}_merchant_id`]}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <TextField
                                    error={!!errors[`merchants_${index}_percentage`]}
                                    label="Percentage (%) *"
                                    value={merchant.percentage || ''}
                                    onChange={(e) => handleMerchantChange(index, 'percentage', e.target.value)}
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    fullWidth
                                    required
                                    helperText={errors[`merchants_${index}_percentage`] || 'Percentage from 0 to 100'}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <IconButton
                                    onClick={() => handleRemoveMerchant(index)}
                                    color="error"
                                    sx={{ mt: 1 }}
                                    aria-label="Remove merchant"
                                >
                                    <Delete />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Paper>
                ))}
                {errors.merchants && <FormHelperText error sx={{ mt: 1 }}>{errors.merchants}</FormHelperText>}
            </Grid>
            
            <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        startIcon={fields.id ? <Save /> : <Add />}
                        size="large"
                    >
                        {fields.id ? 'Update Referral' : 'Create Referral'}
                    </Button>
                </Box>
            </Grid>
        </Grid>
    </React.Fragment>
}

